import React, { useEffect, useRef, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import type { AppProps } from 'next/app';
import { SWRConfig } from 'swr';
import fetchJson from '@datavant/idsb-shared/utils/fetchJson';
import { theme } from '../themes';
import { UiKit, UiKitContext } from '@datavant/uikit';
import { WrappedTopNav } from '../components/TopNav';
import { SideNav } from '../components/SideNav';
import { useRouter } from 'next/router';
import { TOP_NAV_HEIGHT } from '@datavant/idsb-shared/components/size';
import './global.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../components/Login/EmbeddedLogin.css';
import '../components/ActionCenter/supportCenter.css';
import { UserTypeContextContainer } from '../lib/contexts/UserTypeContextContainer';
import Head from 'next/head';
import useUser from '../lib/useUser';
import { Env, getEnv, getEnvVar } from '@datavant/idsb-shared/utils/browserEnvironmentVariables';
import { datadogRum } from '@datadog/browser-rum';
import { SCRIPT, isPendoEnabled } from '../pendo';
import { LDMultiKindContext, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { MultiTenantContextContainer } from '../lib/contexts/MultiTenantContextContainer';
import { PermissionsProvider } from '../lib/contexts/PermissionsProviderContext';
import { TenantType } from '../lib/user/enums';
import head from 'lodash/head';
import { trpc, tRPCProxyClient } from '../utils/trpc';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { shouldUseNewBranding } from '../themes/utils';
import localFlags from '../launchdarkly-defaults.json';
import { datadogLogs } from '@datadog/browser-logs';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { config } from '../okta/config';
import TokenExpiry from '../components/Logout/TokenExpiry';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { isPortalEnvironment } from '../lib/utils/environment';
import { UUID_DATAVANT } from '../lib/utils/consts';
import Script from 'next/script';
import { RingCentral } from '../forms/selfServeOnboarding/sections/RingCentral';
import '@datavant/dart/styles.css';
import { DatavantProvider, datavant } from '@datavant/dart';
import { AgreementModalProvider } from '../lib/contexts/AgreementModalContext';
import { RmV2MigrationGate } from '../components/ProviderTools/RmV2MigrationGate';
import { eventInterceptionPlugin, flagOverridePlugin } from '../utils/launch-darkly';
import LDToolbar from '../components/LDToolbar';

const DATADOG_CONFIG = getEnvVar(getEnv(), 'datadog');
const DATADOG_CLIENT_TOKEN = DATADOG_CONFIG.clientToken;
const DATADOG_SITE = DATADOG_CONFIG.site;
const DATADOG_SERVICE = 'idsb-portal';
const QA_WORKBENCH_BLOCK_KEY = 'block-qa-workbench';

const PAGES_WITHOUT_MAIN_NAV_REGEX = {
    'login': /\/login/,
    'rm-basic': /\/provider\/upload/,
    'rm-basic-upload': /\/provider\/upload\/.*/,
    'logout': /\/logout/,
};
const DD_RUM_STAGES: Env[] = ['staging', 'prod'];

const enableMocking = async () => {
    if (isPortalEnvironment(['prod', 'test']) || !process.env.NEXT_PUBLIC_USE_MSW_BROWSER) {
        return;
    }

    if (typeof window !== 'undefined') {
        const { worker } = await import('../msw/mocks/browser');

        // `worker.start()` returns a Promise that resolves
        // once the Service Worker is up and ready to intercept requests.
        return worker.start();
    }
};

const ldSdkClientSideId = getEnvVar(getEnv(), 'launchDarklySdkClientSideId');

const oktaAuth = new OktaAuth(config);

// eslint-disable-next-line @typescript-eslint/naming-convention
const App = ({ Component, pageProps }: AppProps) => {
    const router = useRouter();
    const ldClient = useLDClient();
    /** Serialized LD context of the last successful identify, so we only re-identify on real change. */
    const identifiedContextRef = useRef<string | null>(null);
    const { data: deviceSession } = trpc.portal.getOrCreateDeviceSession.useQuery();
    const path = head(router.asPath.split('?')) ?? '';
    const isPageWithoutMainTopNav = PAGES_WITHOUT_MAIN_NAV_REGEX['login'].test(path);
    const isPageWithoutMainSideNav =
        Object.values(PAGES_WITHOUT_MAIN_NAV_REGEX).some(regex => regex.test(path)) ||
        path.startsWith('/example');
    const currentUser = useUser()?.user;
    const pageMarginTop = isPageWithoutMainTopNav ? undefined : `${TOP_NAV_HEIGHT}px`;
    const env = getEnv();
    const useNewBranding = shouldUseNewBranding();
    const isPathWorkbench = path.includes('workbench');

    const { data: tenant } = trpc.tenant.getTenant.useQuery(undefined, {
        enabled: !!currentUser,
        staleTime: Infinity, // ✅ Data is always considered fresh
        cacheTime: Infinity, // ✅ Cache never garbage-collected
        refetchOnMount: false, // 🚫 Don't re-fetch on component remount
        refetchOnWindowFocus: false, // 🚫 Don't re-fetch when switching tabs
        refetchOnReconnect: false, // 🚫 Don't re-fetch when network reconnects
    });

    const restoreOriginalUri = async (_oktaAuth: any, originalUri?: string) => {
        window.location.href = window.location.origin + `${originalUri ?? '/'}`;
    };

    // only start msw locally and when the env var is set
    const [mswStarted, setMswStarted] = useState(
        isPortalEnvironment(['prod', 'test']) || !process.env.NEXT_PUBLIC_USE_MSW_BROWSER
    );

    useEffect(() => {
        if (!mswStarted) {
            enableMocking()
                .then(() => setMswStarted(true))
                .catch(() => {
                    console.log('error starting MSW');
                });
        }
    }, []);

    useEffect(() => {
        const setUpLD = async () => {
            const deviceId = deviceSession?.deviceId;
            // The device context is supplementary; the user context is what carries the targeting
            // attributes. Requiring deviceId here used to strand a logged-in user on the anonymous
            // bootstrap context for their whole session whenever the device-session query failed,
            // so identify with whichever half has arrived and re-identify as the rest lands.
            if (!ldSdkClientSideId || !ldClient || (!deviceId && !currentUser?.userUuid)) {
                return;
            }
            const context: LDMultiKindContext = {
                kind: 'multi',
                user: currentUser?.userUuid
                    ? {
                          key: currentUser.userUuid,
                          email: currentUser.email,
                          firstName: currentUser.firstName,
                          lastName: currentUser.lastName,
                          tenantUUID: currentUser.tenantUUID,
                          tenantName: currentUser.tenantName,
                          isLiveDigitalFulfillment:
                              tenant?.digitalMetadata?.isLiveDigitalFulfillment ?? false,
                      }
                    : { key: 'anonymous-context', anonymous: true },
            };
            if (deviceId) {
                context.device = { key: deviceId, anonymous: true };
            }
            // SWR returns a fresh object on every revalidation (window focus, reconnect), so compare
            // on content: identify is a network round trip that re-evaluates every flag.
            const signature = JSON.stringify(context);
            if (identifiedContextRef.current !== signature) {
                identifiedContextRef.current = signature;
                await ldClient.identify(context);
            }
            if (isPathWorkbench) {
                const shouldBlockWorkbench = ldClient.variation(QA_WORKBENCH_BLOCK_KEY, false);
                if (shouldBlockWorkbench) {
                    await router.push('/all');
                }
            }
        };
        void setUpLD();
    }, [ldClient, currentUser, deviceSession?.deviceId, tenant?.digitalMetadata?.isLiveDigitalFulfillment]);

    // always initialize DD browser logs so we can debug/view them locally
    datadogLogs.init({
        clientToken: DATADOG_CLIENT_TOKEN,
        site: DATADOG_SITE,
        service: DATADOG_SERVICE,
        env: env,
        // Corresponds to the docker image tag
        version: process.env.NEXT_PUBLIC_VERSION,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        storeContextsAcrossPages: true,
        silentMultipleInit: true,
    });
    datadogLogs.logger.setHandler(DD_RUM_STAGES.includes(env) ? 'http' : 'console');

    if (DD_RUM_STAGES.includes(env)) {
        datadogRum.init({
            applicationId: DATADOG_CONFIG.applicationId,
            clientToken: DATADOG_CLIENT_TOKEN,
            site: DATADOG_SITE,
            service: DATADOG_SERVICE,
            env: env,
            // Corresponds to the docker image tag
            version: process.env.NEXT_PUBLIC_VERSION,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 0,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask',
            // Suppresses warnings when DD tries to re-init
            // There's no way to check whether DD is already initialized before this call
            silentMultipleInit: true,
            storeContextsAcrossPages: true,
            allowedTracingUrls: [
                { match: 'https://idsb-portal.datavant-staging.com', propagatorTypes: ['tracecontext'] },
                { match: 'https://idsb-portal.datavant.com', propagatorTypes: ['tracecontext'] },
            ],
        });
        if (currentUser) {
            const user = {
                id: currentUser.userUuid ?? undefined,
                email: currentUser.email,
                name: [currentUser.firstName ?? '', currentUser.lastName ?? ''].join(' '),
                tenantUUID: currentUser.tenantUUID,
                tenantName: currentUser.tenantName,
                usingEmbeddedAuth: currentUser.usingEmbeddedAuth,
            };
            datadogRum.setUser({ ...datadogRum.getUser(), ...user });
            datadogLogs.setUser({ ...datadogLogs.getUser(), ...user });
        }
        if (deviceSession) {
            datadogRum.setUser({ ...datadogRum.getUser(), ...deviceSession });
            datadogLogs.setUser({ ...datadogLogs.getUser(), ...deviceSession });
        }
    }

    useEffect(() => {
        // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
        if (typeof window !== 'undefined' && typeof window['pendo'] !== 'undefined') {
            if (router.pathname === '/provider/login' || router.pathname.startsWith('/provider/upload')) {
                // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
                window['pendo'].initialize({
                    visitor: {
                        id: '',
                        device_id: deviceSession?.deviceId,
                        email: '',
                        full_name: '',
                        test_key: '123',
                        is_smart_request_user: false,
                        is_life_science_user: false,
                    },

                    account: {
                        id: '',
                        name: '',
                        device_id: deviceSession?.deviceId,
                    },
                });
            } else if (currentUser && (tenant || currentUser.isTrueDatavanter)) {
                // Initialize once we have a user. Customer users have a `tenantUUID`, so we wait for
                // their `tenant` to load to attach account metadata. Internal Datavant staff (e.g. CS
                // Action Center operators) have no customer tenant and `getTenant` returns null for
                // them, so there is nothing to wait for — initialize immediately so their sessions and
                // events are still captured instead of being dropped as uninitialized (isReady() ===
                // false). Keying off `isTrueDatavanter` (rather than a missing tenantUUID) also avoids
                // initializing for logged-out/loading users, which likewise have no tenantUUID.
                // This function creates anonymous visitor IDs in Pendo unless you change
                // the visitor id field to use your app's values
                // This function uses the placeholder 'ACCOUNT-UNIQUE-ID' value for account ID
                // unless you change the account id field to use your app's values
                // Call this function after users are authenticated in your app and
                //  your visitor and account id values are available
                // Please use Strings, Numbers, or Bools for value types.
                // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
                window['pendo'].initialize({
                    visitor: {
                        id: currentUser.userUuid,
                        email: currentUser.email,
                        full_name: [currentUser.firstName ?? '', currentUser.lastName ?? ''].join(' '),
                        // You can add any additional visitor level key-values here,
                        // as long as it's not one of the above reserved names.
                        test_key: '123',
                        is_smart_request_user: false,
                        is_datavant_internal: currentUser.isTrueDatavanter,
                        is_live_digital_fulfillment: tenant?.digitalMetadata?.isLiveDigitalFulfillment,
                        device_id: deviceSession?.deviceId,
                    },

                    account: {
                        // Internal Datavant staff have no customer tenant record; reuse the app-wide
                        // `UUID_DATAVANT` sentinel (see lib/utils/consts.ts) so their activity groups
                        // under the same "Datavant" account the rest of the app already uses for them.
                        id: currentUser.tenantUUID ?? UUID_DATAVANT,
                        name: currentUser.tenantName ?? 'Datavant',
                        // is_paying:    // Recommended if using Pendo Feedback
                        // monthly_value:// Recommended if using Pendo Feedback
                        // planLevel:    // Optional
                        // planPrice:    // Optional
                        // creationDate: // Optional

                        // You can add any additional account level key-values here,
                        // as long as it's not one of the above reserved names.
                        device_id: deviceSession?.deviceId,
                    },
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentUser,
        deviceSession?.deviceId,
        // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
        typeof window !== 'undefined' && typeof window['pendo'] !== 'undefined',
        tenant?.digitalMetadata?.isLiveDigitalFulfillment,
    ]);

    return mswStarted ? (
        <SWRConfig
            value={{
                fetcher: fetchJson,
                onError: err => {
                    console.error(err);
                },
                // dedupe requests with the same key in this time span in milliseconds
                dedupingInterval: 10000,
            }}
        >
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                <ChakraProvider resetCSS={true} theme={theme}>
                    <UiKitContext.Provider value={{ useNewBranding }}>
                        {isPendoEnabled() && (
                            <div
                                dangerouslySetInnerHTML={{
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    __html: SCRIPT,
                                }}
                            />
                        )}
                        <ReactQueryDevtools initialIsOpen={false} />
                        <Head>
                            <title>Datavant</title>
                        </Head>
                        <DatavantProvider theme={datavant}>
                            <UserTypeContextContainer>
                                <MultiTenantContextContainer>
                                    <PermissionsProvider user={currentUser}>
                                        <AgreementModalProvider>
                                            {!isPageWithoutMainTopNav ? <WrappedTopNav /> : null}
                                            <UiKit.Chakra.HStack
                                                align={'stretch'}
                                                spacing={0}
                                                mt={pageMarginTop}
                                                flex={1}
                                            >
                                                <Script
                                                    src={RingCentral.ScriptURL}
                                                    strategy={'lazyOnload'}
                                                    onLoad={RingCentral.init}
                                                />
                                                <Script
                                                    src="https://demo.docusign.net/clickapi/sdk/latest/docusign-click.js"
                                                    strategy={'afterInteractive'}
                                                />
                                                {!isPageWithoutMainSideNav &&
                                                    currentUser &&
                                                    !currentUser.tenantTypes?.includes(
                                                        TenantType.ONBOARDING
                                                    ) && <SideNav />}
                                                <UiKit.Chakra.VStack
                                                    spacing={UiKit.consistency.buffer}
                                                    flex={1}
                                                    align={'stretch'}
                                                    justify={'flex-start'}
                                                >
                                                    {isPortalEnvironment(['dev']) ? <LDToolbar /> : null}
                                                    {currentUser?.isProviderUser ? (
                                                        <RmV2MigrationGate>
                                                            <Component {...pageProps} />
                                                        </RmV2MigrationGate>
                                                    ) : (
                                                        <Component {...pageProps} />
                                                    )}
                                                    <TokenExpiry
                                                        isPageWithoutNav={
                                                            isPageWithoutMainTopNav &&
                                                            isPageWithoutMainSideNav
                                                        }
                                                    />
                                                </UiKit.Chakra.VStack>
                                            </UiKit.Chakra.HStack>
                                        </AgreementModalProvider>
                                    </PermissionsProvider>
                                </MultiTenantContextContainer>
                            </UserTypeContextContainer>
                        </DatavantProvider>
                    </UiKitContext.Provider>
                </ChakraProvider>
            </Security>
        </SWRConfig>
    ) : (
        <></>
    );
};

// Initialize LaunchDarkly with bootstrapped defaults
const clientOptions = {
    clientSideID: ldSdkClientSideId,
    options: {
        bootstrap: localFlags.flagValues,
        plugins: isPortalEnvironment(['dev']) ? [flagOverridePlugin, eventInterceptionPlugin] : [],
    },
};

export default ldSdkClientSideId === ''
    ? trpc.withTRPC(App)
    : withLDProvider(clientOptions)(trpc.withTRPC(App));
